import { useState, type FormEvent, type ReactNode } from 'react';
import { Eye, FileCheck, FileDown, Info, Pencil, RotateCcw, Stamp } from 'lucide-react';
import { BoutonPrincipal, BoutonSecondaire, Chargement, MessageErreur } from '../ui/Primitives';
import { Modale } from '../ui/Modale';
import { useToast } from '../ui/useToast';
import { ErreurApi } from '../../lib/api';
import { detailsRefus } from '../../lib/erreurs-api';
import { aujourdhui } from '../../lib/dates-civiles';
import {
  ouvrirApercuCertificat,
  urlFichierCertificat,
  useCertificatEcheance,
  useGenererCertificat,
  type CertificatDeLEcheance as Certificat,
  type ContenuCertificat,
  type UniteDuree,
} from '../../lib/useCertificats';
import type { Echeance } from '../../lib/useFinancements';

const CLASSE_CHAMP =
  'min-h-[44px] w-full min-w-0 rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm text-codialis-navy focus:border-codialis-sky focus:outline-none focus:ring-2 focus:ring-codialis-sky/20';

const CLASSE_LIEN_BOUTON =
  'inline-flex min-h-[44px] items-center justify-center rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50';

function jourFrancais(iso: string): string {
  const [annee, mois, jour] = iso.slice(0, 10).split('-');
  return `${jour}/${mois}/${annee}`;
}

function dateHeure(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Indian/Reunion',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(iso));
}

function messageRefus(erreur: unknown, defaut: string): string {
  const detail = detailsRefus(erreur)[0];
  if (detail) return detail.message;
  return erreur instanceof ErreurApi ? erreur.message : defaut;
}

/** « 6.00 » → « 6 », « 6.33 » → « 6,33 » : la durée telle qu'on l'écrit. */
function dureeLisible(valeur: string): string {
  const nombre = Number(valeur);
  return Number.isFinite(nombre) ? String(nombre).replace('.', ',') : valeur;
}

/**
 * Le certificat de réalisation d'une échéance (TF-412), sous l'échéance de
 * l'échéancier : à partir de la deuxième, il se joint à sa facture. Établi, il
 * se télécharge et se corrige ; à établir, il s'établit dès le dernier jour de
 * l'échéance. Ce qui l'empêche vient de l'API.
 */
export function CertificatDeLEcheance({ echeance }: { echeance: Echeance }): JSX.Element | null {
  const [ouverte, setOuverte] = useState(false);
  const { certificat } = echeance;
  if (!certificat) return null;
  const { etabli, refus } = certificat;

  return (
    <div className="w-full min-w-0 rounded-lg border border-dashed border-gray-200 px-3 py-2">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        <div className="min-w-0 sm:flex-1">
          <p className="flex gap-1.5 text-xs font-semibold text-codialis-navy">
            <FileCheck className="mt-px h-3.5 w-3.5 shrink-0 text-codialis-sky" aria-hidden />
            <span className="min-w-0">
              Certificat de réalisation{' '}
              <span className={etabli ? 'text-emerald-700' : 'text-amber-700'}>
                · {etabli ? `du ${jourFrancais(etabli.date)}` : 'à établir'}
              </span>
            </span>
          </p>
          {etabli && (
            <p className="mt-0.5 break-words text-[11px] text-codialis-gray">
              {etabli.resume} · produit le {dateHeure(etabli.genereLe)}
              {etabli.generePar ? ` par ${etabli.generePar}` : ''}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
          {etabli && (
            <a
              href={urlFichierCertificat(etabli.id)}
              target="_blank"
              rel="noreferrer"
              className={CLASSE_LIEN_BOUTON}
              aria-label={`Télécharger le certificat de réalisation de l’échéance n° ${echeance.rang}`}
            >
              <FileDown className="mr-1.5 h-4 w-4" aria-hidden />
              Télécharger
            </a>
          )}
          {etabli ? (
            <BoutonSecondaire onClick={() => setOuverte(true)} disabled={refus !== null}>
              <Pencil className="mr-1.5 h-4 w-4" aria-hidden />
              Corriger
            </BoutonSecondaire>
          ) : (
            <BoutonSecondaire onClick={() => setOuverte(true)} disabled={refus !== null}>
              <FileCheck className="mr-1.5 h-4 w-4" aria-hidden />
              Établir le certificat
            </BoutonSecondaire>
          )}
        </div>
      </div>
      {etabli?.aRegenerer && (
        <p className="mt-1.5 break-words rounded-lg bg-amber-50 px-2.5 py-1.5 text-[11px] font-semibold text-amber-800">
          {etabli.aRegenerer}
        </p>
      )}
      {refus && <p className="mt-1.5 break-words text-[11px] text-codialis-gray">{refus}</p>}
      {ouverte && <ModaleCertificat echeanceId={echeance.id} rang={echeance.rang} onFermer={() => setOuverte(false)} />}
    </div>
  );
}

function ModaleCertificat({
  echeanceId,
  rang,
  onFermer,
}: {
  echeanceId: string;
  rang: number;
  onFermer: () => void;
}): JSX.Element {
  const lecture = useCertificatEcheance(echeanceId);

  return (
    <Modale
      ouverte
      titre={`Certificat de réalisation · échéance n° ${rang}`}
      sousTitre="Proposé depuis le contrat, à vérifier avant de le produire"
      onFermer={onFermer}
      largeur="max-w-2xl"
    >
      {lecture.isLoading && (
        <div className="p-4 sm:p-6">
          <Chargement />
        </div>
      )}
      {lecture.isError && (
        <div className="p-4 sm:p-6">
          <MessageErreur message="Le certificat n’a pas pu être lu." />
        </div>
      )}
      {lecture.data && <FormulaireCertificat donnees={lecture.data} onFermer={onFermer} />}
    </Modale>
  );
}

function Champ({ libelle, id, children, aide }: { libelle: string; id: string; children: ReactNode; aide?: string }) {
  return (
    <div className="min-w-0">
      <label htmlFor={id} className="mb-1 block text-xs font-semibold text-codialis-navy">
        {libelle}
      </label>
      {children}
      {aide && <p className="mt-1 text-[11px] text-codialis-gray">{aide}</p>}
    </div>
  );
}

function versSaisie(contenu: ContenuCertificat): ContenuCertificat {
  return { ...contenu, dureeRealisee: dureeLisible(contenu.dureeRealisee) };
}

function FormulaireCertificat({ donnees, onFermer }: { donnees: Certificat; onFermer: () => void }): JSX.Element {
  const toast = useToast();
  const generer = useGenererCertificat();
  const { echeance, certificat, proposition } = donnees;
  const [saisie, setSaisie] = useState<ContenuCertificat>(() =>
    versSaisie(certificat?.contenu ?? proposition),
  );
  const [erreur, setErreur] = useState<string | null>(null);
  const [apercuEnCours, setApercuEnCours] = useState(false);

  const changer = <K extends keyof ContenuCertificat>(cle: K, valeur: ContenuCertificat[K]): void => {
    setSaisie((avant) => ({ ...avant, [cle]: valeur }));
    setErreur(null);
  };
  const champ = (cle: Exclude<keyof ContenuCertificat, 'natureAction' | 'uniteDuree'>) => ({
    id: `certificat-${cle}`,
    value: saisie[cle],
    onChange: (evenement: { target: { value: string } }) => changer(cle, evenement.target.value),
  });

  const apercu = (): void => {
    setErreur(null);
    setApercuEnCours(true);
    ouvrirApercuCertificat(echeance.id, saisie)
      .catch((refus: unknown) => setErreur(messageRefus(refus, 'L’aperçu n’a pas pu être produit.')))
      .finally(() => setApercuEnCours(false));
  };

  const soumettre = (evenement: FormEvent): void => {
    evenement.preventDefault();
    setErreur(null);
    generer.mutate(
      { echeanceId: echeance.id, contenu: saisie },
      {
        onSuccess: () => {
          toast(
            certificat
              ? `Certificat de l’échéance n° ${echeance.rang} régénéré.`
              : `Certificat de l’échéance n° ${echeance.rang} produit : joignez-le à sa facture.`,
          );
          onFermer();
        },
        onError: (refus) => setErreur(messageRefus(refus, 'Le certificat n’a pas pu être produit.')),
      },
    );
  };

  return (
    <form onSubmit={soumettre} className="space-y-4 p-4 sm:p-6" aria-label="Contenu du certificat de réalisation">
      <p className="rounded-xl bg-codialis-sand px-3 py-2 text-sm text-codialis-navy">
        <span className="text-xs text-codialis-gray">Mme/M. </span>
        <span className="font-semibold">{donnees.beneficiaire}</span>
        <span className="block text-[11px] text-codialis-gray">
          Échéance n° {echeance.rang} du {jourFrancais(echeance.dateDebut)} au {jourFrancais(echeance.dateFin)}. Le
          nom vient du dossier : il se corrige là.
        </span>
      </p>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Champ libelle="Salarié(e) de l’entreprise" id="certificat-entreprise">
          <input {...champ('entreprise')} required maxLength={200} className={CLASSE_CHAMP} />
        </Champ>
        <Champ libelle="A suivi l’action" id="certificat-intituleAction">
          <input {...champ('intituleAction')} required maxLength={300} className={CLASSE_CHAMP} />
        </Champ>
      </div>

      <fieldset className="min-w-0">
        <legend className="mb-1 text-xs font-semibold text-codialis-navy">Nature de l’action</legend>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {donnees.natures.map((nature) => (
            <label
              key={nature.valeur}
              className={`relative flex min-h-[44px] cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-sm ${
                saisie.natureAction === nature.valeur
                  ? 'border-codialis-sky bg-codialis-sky/5 font-semibold text-codialis-navy'
                  : 'border-gray-200 bg-white text-codialis-navy'
              }`}
            >
              <input
                type="radio"
                name="certificat-nature"
                value={nature.valeur}
                checked={saisie.natureAction === nature.valeur}
                onChange={() => changer('natureAction', nature.valeur)}
                className="h-4 w-4 shrink-0 accent-codialis-navy"
              />
              <span className="min-w-0">{nature.libelle}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Champ libelle="Déroulée du" id="certificat-periodeDebut">
          <input {...champ('periodeDebut')} type="date" required className={CLASSE_CHAMP} />
        </Champ>
        <Champ libelle="Au" id="certificat-periodeFin">
          <input {...champ('periodeFin')} type="date" required className={CLASSE_CHAMP} />
        </Champ>
        <Champ
          libelle="Durée réalisée"
          id="certificat-dureeRealisee"
          aide="En mois pour une formation par apprentissage, en heures sinon."
        >
          <input
            {...champ('dureeRealisee')}
            inputMode="decimal"
            required
            placeholder="6"
            className={`${CLASSE_CHAMP} text-right tabular-nums`}
          />
        </Champ>
        <Champ libelle="Unité" id="certificat-uniteDuree">
          <select
            id="certificat-uniteDuree"
            value={saisie.uniteDuree}
            onChange={(e) => changer('uniteDuree', e.target.value as UniteDuree)}
            className={CLASSE_CHAMP}
          >
            {donnees.unites.map((unite) => (
              <option key={unite.valeur} value={unite.valeur}>
                {unite.libelle}
              </option>
            ))}
          </select>
        </Champ>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Champ libelle="Fait à" id="certificat-lieuSignature">
          <input {...champ('lieuSignature')} required maxLength={100} className={CLASSE_CHAMP} />
        </Champ>
        <Champ libelle="Le" id="certificat-dateCertificat">
          <input
            {...champ('dateCertificat')}
            type="date"
            required
            min={saisie.periodeFin || undefined}
            max={aujourdhui()}
            className={CLASSE_CHAMP}
          />
        </Champ>
        <Champ libelle="Signataire" id="certificat-signataireNom" aide="Civilité comprise : « M. MELADE Thierry ».">
          <input {...champ('signataireNom')} required maxLength={120} className={CLASSE_CHAMP} />
        </Champ>
        <Champ libelle="Qualité" id="certificat-signataireQualite">
          <input {...champ('signataireQualite')} required maxLength={120} className={CLASSE_CHAMP} />
        </Champ>
      </div>

      <p className="flex gap-2 text-xs text-codialis-gray">
        {donnees.cachet ? (
          <Stamp className="mt-px h-4 w-4 shrink-0 text-codialis-sky" aria-hidden />
        ) : (
          <Info className="mt-px h-4 w-4 shrink-0 text-codialis-sky" aria-hidden />
        )}
        <span className="min-w-0">
          {donnees.cachet
            ? 'Le cachet et la signature déposés dans les paramètres de facturation seront imprimés dans le cadre.'
            : 'Aucune image de cachet n’est déposée dans les paramètres de facturation : le cadre restera vide, à signer à la main.'}
        </span>
      </p>

      {certificat && (
        <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-codialis-gray">
          <span className="min-w-0">
            Produit le {dateHeure(certificat.genereLe)}
            {certificat.generePar ? ` par ${certificat.generePar}` : ''}. Le régénérer remplace le fichier.
          </span>
          <button
            type="button"
            onClick={() => {
              setSaisie(versSaisie(proposition));
              setErreur(null);
            }}
            className="inline-flex min-h-[40px] items-center gap-1 rounded-lg px-2 font-semibold text-codialis-sky hover:bg-codialis-sky/5"
          >
            <RotateCcw className="h-3.5 w-3.5" aria-hidden />
            Repartir de la proposition
          </button>
        </div>
      )}

      {erreur && (
        <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
          {erreur}
        </p>
      )}
      {donnees.refus && (
        <p className="rounded-xl bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">{donnees.refus}</p>
      )}

      <div className="flex flex-wrap items-center justify-end gap-2">
        <BoutonSecondaire onClick={onFermer} disabled={generer.isPending}>
          Annuler
        </BoutonSecondaire>
        <BoutonSecondaire onClick={apercu} disabled={apercuEnCours || donnees.refus !== null}>
          <Eye className="mr-1.5 h-4 w-4" aria-hidden />
          {apercuEnCours ? 'Aperçu…' : 'Aperçu'}
        </BoutonSecondaire>
        <BoutonPrincipal type="submit" disabled={generer.isPending || donnees.refus !== null}>
          <FileCheck className="mr-1.5 h-4 w-4" aria-hidden />
          {generer.isPending ? 'Production…' : certificat ? 'Régénérer le certificat' : 'Produire le certificat'}
        </BoutonPrincipal>
      </div>
    </form>
  );
}
